export const PROJECT_MEMBER_ROLES = [
  { value: 'PRIMARY', label: 'Primary' },
  { value: 'SECONDARY', label: 'Secondary' },
  { value: 'SUPPORT', label: 'Support' },
  { value: 'OVERSIGHT', label: 'Oversight' },
] as const;

export type ProjectMemberRole = (typeof PROJECT_MEMBER_ROLES)[number]['value'];

export type ProjectListFilters = {
  search?: string;
  isActive?: boolean;
};

export type ProjectListItem = {
  projectId: number;
  projectName: string;
  description: string | null;
  memberCount: number;
  statusCode: string | null;
  statusName: string | null;
  statusLabel: string;
  isActive: boolean | null;
  createdAt: string | null;
  rowVersion: number | null;
};

export type ProjectSearchResult = {
  items: ProjectListItem[];
  page: number;
  pageSize: number;
  totalItems: number;
  totalPages: number;
};

export type ProjectMember = {
  projectMemberId: number | null;
  employeeId: number;
  employeeNumber: string | null;
  employeeName: string;
  memberRole: ProjectMemberRole;
};

export type ProjectColumn = {
  projectColumnId: number | null;
  columnName: string;
  displayOrder: number;
  taskLimit: number | null;
  isCompletedColumn: boolean;
  isDefaultColumn: boolean;
  requiresTaskDetails: boolean;
  taskCount?: number;
};

export type ProjectAssignee = {
  employeeId: number;
  employeeName: string;
  memberRole: ProjectMemberRole | null;
  displayOrder: number;
};

export type ProjectSubtask = {
  subtaskId: number | null;
  taskId: number | null;
  subtaskName: string;
  displayOrder: number;
  isCompleted: boolean;
  assignees: ProjectAssignee[];
};

export type ProjectTask = {
  taskId: number;
  projectId: number;
  projectColumnId: number;
  taskName: string;
  assignedEmployeeId: number | null;
  assignedEmployeeName: string | null;
  assignedMemberRole: ProjectMemberRole | null;
  assignees: ProjectAssignee[];
  dueDate: string | null;
  completedDate: string | null;
  timeEstimateMinutes: number | null;
  timeSpentMinutes: number;
  description: string | null;
  displayOrder: number;
  subtaskTotal: number;
  subtaskCompleted: number;
  subtasks: ProjectSubtask[];
  createdByEmployeeId?: number | null;
  createdByName?: string | null;
};

export type ProjectBoardColumn = ProjectColumn & {
  taskCount: number;
  tasks: ProjectTask[];
};

export type ProjectBoard = {
  projectId: number;
  projectName: string;
  description: string | null;
  members: ProjectMember[];
  columns: ProjectBoardColumn[];
};

export type CompletedFocusBoard = {
  selectedDate: string;
  previousDate: string;
  todayTasks: ProjectTask[];
  yesterdayTasks: ProjectTask[];
};

export type ProjectTaskCreateInput = {
  projectColumnId: number;
  taskName: string;
  assignedEmployeeId?: number | null;
  assignedEmployeeIds?: number[];
  dueDate?: string | null;
  /** Required when creating directly in a completed (Done) column. */
  completedDate?: string | null;
  timeEstimate?: string | null;
  timeSpent?: string | null;
  description?: string | null;
  subtasks: Array<{ subtaskName: string; assignedEmployeeIds?: number[] }>;
};

export type ProjectTaskUpdateInput = {
  taskName: string;
  assignedEmployeeIds: number[];
  dueDate?: string | null;
  completedDate?: string | null;
  timeEstimate?: string | null;
  timeSpent?: string | null;
  description?: string | null;
};

export type ProjectSubtaskWriteInput = {
  subtaskName: string;
  assignedEmployeeIds?: number[];
  isCompleted?: boolean;
};

export type TaskTimeLogSource = 'MANUAL' | 'POMODORO' | 'DEEP_FOCUS';

export type TaskTimeLog = {
  timeLogId: number;
  taskId: number;
  employeeId: number;
  employeeName: string;
  startedAt: string;
  endedAt: string;
  durationMinutes: number;
  source: TaskTimeLogSource;
};

export type TaskTimeLogCreateInput = {
  date: string;
  startTime: string;
  endTime: string;
  source?: TaskTimeLogSource;
  /** Only sent by HR Admins / Team Leads; employees always log as themselves. */
  employeeId?: number;
};

/**
 * A finished Pomodoro / Deep Focus session. The API records it for the
 * signed-in employee on the project / task in the URL; only the focus type and
 * the session's instants are sent.
 */
export type TaskFocusSessionInput = {
  focusType: 'POMODORO' | 'DEEP_FOCUS';
  /** ISO 8601 instants. */
  startedAt: string;
  endedAt: string;
};

export type TaskTimeLogList = {
  logs: TaskTimeLog[];
  timeSpentMinutes: number;
};

/** ALL = HR Admin, CREATOR = Team Lead (own projects), MEMBER = assigned only. */
export type ProjectScope = 'ALL' | 'CREATOR' | 'MEMBER';

export type ProjectModuleCapabilities = {
  scope: ProjectScope;
  canViewProjects: boolean;
  canCreateProject: boolean;
  canUpdateProject: boolean;
  /** Edit Project → add members (HR Admin, or the Team Lead who created it). */
  canEditProjectMembers: boolean;
  canManageTasks: boolean;
  /** View / download the task report (rows limited to the scope by the API). */
  canViewTaskReport: boolean;
};

export type ProjectCreateInput = {
  projectName: string;
  description?: string | null;
  members: Array<{
    employeeId: number;
    role: ProjectMemberRole;
  }>;
  columns: Array<{
    columnName: string;
    taskLimit: number | null;
    isCompletedColumn: boolean;
    isDefaultColumn: boolean;
    displayOrder: number;
  }>;
};

export type ProjectDetails = {
  projectId: number;
  projectName: string;
  description: string | null;
  statusCode: string | null;
  statusName: string | null;
  statusLabel: string;
  isActive: boolean | null;
  createdAt: string | null;
  updatedAt: string | null;
  rowVersion: number | null;
  members: ProjectMember[];
  columns: ProjectColumn[];
};

export type ProjectAssignableEmployee = {
  employeeId: number;
  employeeNumber: string | null;
  displayName: string;
};

export type ProjectMembersAddInput = {
  members: Array<{
    employeeId: number;
    role: ProjectMemberRole;
  }>;
};

export type ProjectMemberDraft = {
  key: string;
  employeeId: number;
  employeeName: string;
  role: ProjectMemberRole;
};

export type ProjectColumnDraft = {
  key: string;
  columnName: string;
  taskLimit: string;
  isCompletedColumn: boolean;
  isDefaultColumn: boolean;
  fixedSlot?: 'start' | 'end' | null;
};
